import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

// Same codes as backend/schemas/interventionRating.schema.ts; labels are i18n keys in the components.
// 3 stars or less qualifies what went wrong; 4 or more what was appreciated.
export const RATING_REASONS_NEGATIVE = ['late', 'incomplete', 'not_cleaned', 'communication', 'instructions_ignored', 'damage'] as const;
export const RATING_REASONS_POSITIVE = ['punctual', 'quality_work', 'clean_site', 'good_communication', 'helpful_advice', 'speed'] as const;
export const RATING_DETAILS_NEGATIVE = ['late_arrival', 'not_finished', 'store_not_restored', 'result_mismatch'] as const;
export const RATING_DETAILS_POSITIVE = ['work_as_requested', 'punctual_professional', 'store_left_clean', 'fast_well_explained'] as const;
export const RATING_REASONS = [...RATING_REASONS_NEGATIVE, ...RATING_REASONS_POSITIVE] as const;
export const RATING_DETAILS = [...RATING_DETAILS_NEGATIVE, ...RATING_DETAILS_POSITIVE] as const;
export const NEGATIVE_MAX_SCORE = 3;

export type RatingReason = (typeof RATING_REASONS)[number];
export type RatingDetail = (typeof RATING_DETAILS)[number];

export interface InterventionRating {
  issueKey: string;
  // id is null when the group names several technicians (count > 1).
  technician: { id: number | null; name: string; count: number };
  score: number | null;
  reasons: RatingReason[];
  detail: RatingDetail | null;
  ratedAt: string | null;
  // Whether the caller is the ticket reporter allowed to rate.
  canRate: boolean;
}

export interface RateInterventionBody {
  score: number;
  reasons: RatingReason[];
  detail: RatingDetail | null;
}

const jsonHeaders = { 'Content-Type': 'application/json' };
const ratingPath = (issueKey: string) => `/issues/${encodeURIComponent(issueKey)}/rating`;

// 403/404 mean "no card to show" for this caller, not an error; so does a payload without a technician.
export const fetchRating = async (issueKey: string): Promise<InterventionRating | null> => {
  const res = await fetchWithAuth(ratingPath(issueKey));
  if (res.status === 403 || res.status === 404) return null;
  const rating = await unwrap<InterventionRating | null>(res, 'Failed to fetch the intervention rating');
  return rating?.technician ? rating : null;
};

export const rateIntervention = async (issueKey: string, body: RateInterventionBody): Promise<InterventionRating> => {
  const res = await fetchWithAuth(ratingPath(issueKey), { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(body) });
  return unwrap<InterventionRating>(res, 'Failed to save the intervention rating');
};

export const deleteRating = async (issueKey: string): Promise<InterventionRating> => {
  const res = await fetchWithAuth(ratingPath(issueKey), { method: 'DELETE' });
  return unwrap<InterventionRating>(res, 'Failed to delete the intervention rating');
};
